import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Alert, Snackbar,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, TextField, InputAdornment,
  Autocomplete, Avatar, List, ListItem, ListItemText, ListItemAvatar,
  ListItemSecondaryAction, Stack, CardActionArea
} from '@mui/material';
import {
  PersonAdd, Delete, Edit, Save, Search, LocalGasStation,
  Person, Refresh, Visibility, VisibilityOff
} from '@mui/icons-material';
import api from '../services/api';
import { useMobile } from '../hooks/useMobile';

export default function LetturistiPage() {
  const { isMobile } = useMobile();
  const [letturisti, setLetturisti] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog assegnazione
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [allPlants, setAllPlants] = useState([]);
  const [plantSearch, setPlantSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Dialog creazione letturista
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', nome: '', email: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/letturista/users');
      setLetturisti(res.data.data || []);
    } catch (err) {
      setError('Errore nel caricamento letturisti');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAllPlants = async () => {
    try {
      const res = await api.get('/plants?limit=10000');
      setAllPlants(res.data.data || res.data || []);
    } catch (_) {}
  };

  const openAssignments = async (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    try {
      const [assignRes] = await Promise.all([
        api.get(`/letturista/assignments/${user.id}`),
        allPlants.length === 0 ? loadAllPlants() : Promise.resolve()
      ]);
      setAssignments(assignRes.data.data?.map(a => a.plant_id) || []);
    } catch (err) {
      setError('Errore caricamento assegnazioni');
    }
  };

  const togglePlant = (plantId) => {
    setAssignments(prev =>
      prev.includes(plantId) ? prev.filter(id => id !== plantId) : [...prev, plantId]
    );
  };

  const selectAll = () => {
    setAssignments(filteredPlants.map(p => p.id));
  };

  const deselectAll = () => {
    setAssignments([]);
  };

  const saveAssignments = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.put(`/letturista/assignments/${selectedUser.id}`, {
        plantIds: assignments
      });
      setSuccess(`Assegnazioni aggiornate per ${selectedUser.nome} ${selectedUser.cognome}`);
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore salvataggio assegnazioni');
    }
    setSaving(false);
  };

  const handleCreateUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      setError('Username e password sono obbligatori');
      return;
    }
    setCreating(true);
    try {
      await api.post('/letturista/users', newUser);
      setSuccess(`Letturista "${newUser.username}" creato con successo`);
      setCreateOpen(false);
      setNewUser({ username: '', password: '', nome: '', email: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella creazione');
    }
    setCreating(false);
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Disattivare il letturista "${user.nome || user.username}"? Le assegnazioni verranno rimosse.`)) return;
    try {
      await api.delete(`/letturista/users/${user.id}`);
      setSuccess(`Letturista "${user.username}" disattivato`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella disattivazione');
    }
  };

  const filteredPlants = allPlants.filter(p => {
    if (!plantSearch.trim()) return true;
    const q = plantSearch.toLowerCase();
    return (p.nome || '').toLowerCase().includes(q) ||
      (p.citta || '').toLowerCase().includes(q) ||
      (p.indirizzo || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 2, gap: 1 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
          Gestione Letturisti
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'flex-end' : 'flex-end' }}>
          <Button startIcon={<PersonAdd />} onClick={() => setCreateOpen(true)} variant="contained" size="small">
            {isMobile ? 'Nuovo' : 'Nuovo Letturista'}
          </Button>
          <Button startIcon={<Refresh />} onClick={loadData} variant="outlined" size="small">
            {isMobile ? '' : 'Aggiorna'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Crea letturisti e assegna loro gli impianti da rilevare nella PWA.
      </Typography>

      {letturisti.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              Nessun letturista trovato. Crea un utente con ruolo "letturista" nelle impostazioni.
            </Typography>
          </CardContent>
        </Card>
      ) : isMobile ? (
        /* Mobile: Card list */
        <Stack spacing={1}>
          {letturisti.map(user => (
            <Card key={user.id} variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.85rem' }}>
                      {(user.nome || user.username || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {[user.nome, user.cognome].filter(Boolean).join(' ') || user.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        @{user.username}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    icon={<LocalGasStation />}
                    label={user.plant_count || 0}
                    color={user.plant_count > 0 ? 'primary' : 'default'}
                    variant={user.plant_count > 0 ? 'filled' : 'outlined'}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                  <Button startIcon={<Edit />} size="small" onClick={() => openAssignments(user)} variant="outlined">
                    Assegna
                  </Button>
                  <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        /* Desktop: Table */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Letturista</TableCell>
                <TableCell>Username</TableCell>
                <TableCell align="center">Impianti assegnati</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {letturisti.map(user => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {(user.nome || user.username || '?')[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>
                        {[user.nome, user.cognome].filter(Boolean).join(' ') || user.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{user.username}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<LocalGasStation />}
                      label={user.plant_count || 0}
                      color={user.plant_count > 0 ? 'primary' : 'default'}
                      variant={user.plant_count > 0 ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      startIcon={<Edit />}
                      size="small"
                      onClick={() => openAssignments(user)}
                    >
                      Assegna
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteUser(user)}
                      sx={{ ml: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assignment Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Assegna impianti a {selectedUser?.nome} {selectedUser?.cognome}
          <Typography variant="body2" color="text.secondary">
            Seleziona gli impianti che il letturista potrà visualizzare nella PWA
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Cerca impianto..."
              value={plantSearch}
              onChange={(e) => setPlantSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
              }}
              sx={{ flex: 1 }}
            />
            <Button size="small" onClick={selectAll}>Tutti</Button>
            <Button size="small" onClick={deselectAll}>Nessuno</Button>
            <Chip label={`${assignments.length} selezionati`} color="primary" size="small" />
          </Box>

          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List dense disablePadding>
              {filteredPlants.map(plant => (
                <ListItem
                  key={plant.id}
                  button
                  onClick={() => togglePlant(plant.id)}
                  sx={{
                    bgcolor: assignments.includes(plant.id) ? 'action.selected' : 'transparent',
                    borderRadius: 1, mb: 0.5
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={assignments.includes(plant.id)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                  <ListItemText
                    primary={plant.nome || `Impianto #${plant.id}`}
                    secondary={[plant.indirizzo, plant.citta, plant.provincia].filter(Boolean).join(', ')}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={saveAssignments}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            Salva assegnazioni
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Creazione Letturista */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuovo Letturista</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome completo"
            value={newUser.nome}
            onChange={(e) => setNewUser(prev => ({ ...prev, nome: e.target.value }))}
            margin="normal"
            autoFocus
          />
          <TextField
            fullWidth
            label="Username *"
            value={newUser.username}
            onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
            margin="normal"
            helperText="Usato per il login nella PWA"
          />
          <TextField
            fullWidth
            label="Password *"
            type={showPassword ? 'text' : 'password'}
            value={newUser.password}
            onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
            margin="normal"
            helperText="Minimo 4 caratteri"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={creating || !newUser.username.trim() || !newUser.password.trim()}
            startIcon={creating ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            Crea Letturista
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );
}
